import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Copy, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Info, Package, FlaskConical, Tag, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import SearchableSelect from './SearchableSelect';
import RacikanRow from './RacikanRow';
import InlinePriceEditor from './InlinePriceEditor';
import DiscoverySetItems from './DiscoverySetItems';
import { needsLiterConversion, formatGramMl } from '@/lib/unitConverter';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(Math.round(n || 0));
const getHargaBeli = (item) => item?.harga_rupiah || 0;
const getHargaJual = (item) => item?.harga_jual || (getHargaBeli(item) * 1.5);

const hitungJasa = (ml) => {
  if (!ml || ml === 0) return 0;
  if (ml <= 10) return 4000;
  if (ml <= 30) return 10000;
  if (ml <= 50) return 15000;
  return 20000;
};

const safeFloat = (val) => {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = parseFloat(String(val).replace(/,/g, '.'));
  return isNaN(parsed) ? 0 : parsed;
};

const parseDecimalInput = (val) => {
  if (val === '' || val === undefined || val === null) return undefined;
  if (typeof val === 'number') return val;
  let str = String(val).trim();
  if (str.includes(',') && str.includes('.')) {
    str = str.replace(/\./g, '').replace(',', '.');
  } else if (str.includes(',')) {
    str = str.replace(',', '.');
  }
  const n = parseFloat(str);
  return isNaN(n) ? undefined : n;
};

function DecimalInput({ value, onChange, className, placeholder, ...props }) {
  const [strVal, setStrVal] = useState(value === undefined || value === null || value === '' ? '' : String(value).replace('.', ','));

  useEffect(() => {
    const formatted = value === undefined || value === null || value === '' ? '' : String(value).replace('.', ',');
    const parsedStr = parseDecimalInput(strVal);
    const parsedVal = typeof value === 'number' ? value : parseDecimalInput(value);
    if (parsedStr !== parsedVal) {
      setStrVal(formatted);
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setStrVal(raw);
    if (raw === '') {
      onChange(undefined);
      return;
    }
    const parsed = parseDecimalInput(raw);
    onChange(parsed);
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={strVal}
      onChange={handleChange}
      className={className}
      placeholder={placeholder}
      {...props}
    />
  );
}

const FUNGSI_PRODUK = [
  'Brightening', 'Anti-Aging', 'Acne Care', 'Moisturizing', 
  'Soothing', 'Exfoliating', 'Sun Protection'
];

const toTitleCase = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

import { useSession } from '@/lib/SessionContext';

export default function FinanceInvoiceCard({ product, index, botolList, tutupList, sprayList, bahanList, itemTambahanList = [], fullsetList = [], onChange, onRemove, onDuplicate, canRemove, isDatabaseMode = false }) {
  const { member } = useSession();
  const [expanded, setExpanded] = useState(!product?.nama_product);

  // Derivasi tab awal berdasarkan data yang sudah ada (untuk load data invoice yg sudah di-save)
  const [tabBotol, setTabBotol] = useState(() => {
    const b = botolList.find(x => x.id === product.botol_id);
    return product.botol_dari_client || (b && b.dari_client) ? 'client' : 'satala';
  });
  const [tabTutup, setTabTutup] = useState(() => {
    const t = tutupList.find(x => x.id === product.tutup_id);
    return product.tutup_dari_client || (t && t.dari_client) ? 'client' : 'satala';
  });
  const [tabSpray, setTabSpray] = useState(() => {
    const s = sprayList.find(x => x.id === product.spray_id);
    return product.spray_dari_client || (s && s.dari_client) ? 'client' : 'satala';
  });

  // Sync tabs jika product prop berubah (misal ganti dari luar)
  useEffect(() => {
    const b = botolList.find(x => x.id === product.botol_id);
    if (product.botol_dari_client || (b && b.dari_client)) setTabBotol('client');
    else if (product.botol_id && (!b || !b.dari_client)) setTabBotol('satala');
  }, [product.botol_id, product.botol_dari_client, botolList]);

  useEffect(() => {
    const t = tutupList.find(x => x.id === product.tutup_id);
    if (product.tutup_dari_client || (t && t.dari_client)) setTabTutup('client');
    else if (product.tutup_id && (!t || !t.dari_client)) setTabTutup('satala');
  }, [product.tutup_id, product.tutup_dari_client, tutupList]);

  useEffect(() => {
    const s = sprayList.find(x => x.id === product.spray_id);
    if (product.spray_dari_client || (s && s.dari_client)) setTabSpray('client');
    else if (product.spray_id && (!s || !s.dari_client)) setTabSpray('satala');
  }, [product.spray_id, product.spray_dari_client, sprayList]);

  const botolDariClient = product.botol_dari_client === true;
  const tutupDariClient = product.tutup_dari_client === true;
  const sprayDariClient = product.spray_dari_client === true;

  const botol = botolDariClient ? null : botolList.find(b => b.id === product.botol_id);
  const tutup = tutupDariClient ? null : tutupList.find(t => t.id === product.tutup_id);
  const spray = sprayDariClient ? null : sprayList.find(s => s.id === product.spray_id);
  
  const fullset = fullsetList.find(f => f.id === product.fullset_id);

  const ukuranLabelMl = (product.is_fullset && fullset && fullset.ukuran_label_ml > 0) ? fullset.ukuran_label_ml : (botolDariClient || (product.is_fullset && !botol) ? (product.botol_ukuran_client || 0) : (botol?.ukuran_label_ml ?? 0));
  const dbAktual = (product.is_fullset && fullset && fullset.ukuran_aktual_ml > 0) ? fullset.ukuran_aktual_ml : (botolDariClient || (product.is_fullset && !botol) ? 0 : (botol?.ukuran_aktual_ml ?? 0));
  const ukuranAktualMl = product.custom_ukuran_aktual_ml !== undefined ? product.custom_ukuran_aktual_ml : dbAktual;
  const hasAktual = ukuranAktualMl > 0 && ukuranAktualMl !== ukuranLabelMl;
  const ukuranLabel = (product.ukuran_digunakan === 'aktual' || product.custom_ukuran_aktual_ml !== undefined) && hasAktual ? ukuranAktualMl : ukuranLabelMl;

  const isDiscoverySet = product.is_discovery_set || (product.nama_product && product.nama_product.toLowerCase().includes('discovery set'));
  const totalPersen = (product.racikan || []).reduce((s, r) => s + safeFloat(r.persentase), 0);
  const isValid = isDiscoverySet ? true : Math.abs(totalPersen - 100) < 0.01;


  // HPP / Modal calculation - WITH SNAPSHOT & DRIFT DETECTION
  const isInvoiceSaved = product.botol_harga_beli !== undefined || product.botol_harga !== undefined;
  
  const modalBotol = botolDariClient ? 0 : (isInvoiceSaved ? (product.botol_harga_beli || 0) : getHargaBeli(botol));
  const modalTutup = tutupDariClient ? 0 : (isInvoiceSaved ? (product.tutup_harga_beli || 0) : getHargaBeli(tutup));
  const modalSpray = sprayDariClient ? 0 : (isInvoiceSaved ? (product.spray_harga_beli || 0) : getHargaBeli(spray));
  
  let modalPackaging = product.is_fullset && fullset ? (isInvoiceSaved ? (product.fullset_harga_beli || 0) : fullset.harga_rupiah) : (modalBotol + modalTutup + modalSpray);
  
  // Drift Detection Flags
  const driftBotol = isInvoiceSaved && !botolDariClient && botol && getHargaBeli(botol) !== product.botol_harga_beli;
  const driftTutup = isInvoiceSaved && !tutupDariClient && tutup && getHargaBeli(tutup) !== product.tutup_harga_beli;
  const driftSpray = isInvoiceSaved && !sprayDariClient && spray && getHargaBeli(spray) !== product.spray_harga_beli;
  const driftFullset = isInvoiceSaved && product.is_fullset && fullset && getHargaBeli(fullset) !== (product.fullset_harga_beli || 0);
  
  const driftedItems = [];
  if (driftBotol) driftedItems.push(`Modal Botol (Master: Rp ${fmt(getHargaBeli(botol))} vs Invoice: Rp ${fmt(product.botol_harga_beli)})`);
  if (driftTutup) driftedItems.push(`Modal Tutup (Master: Rp ${fmt(getHargaBeli(tutup))} vs Invoice: Rp ${fmt(product.tutup_harga_beli)})`);
  if (driftSpray) driftedItems.push(`Modal Spray (Master: Rp ${fmt(getHargaBeli(spray))} vs Invoice: Rp ${fmt(product.spray_harga_beli)})`);
  if (driftFullset) driftedItems.push(`Modal Fullset (Master: Rp ${fmt(getHargaBeli(fullset))} vs Invoice: Rp ${fmt(product.fullset_harga_beli)})`);
  const hasDrift = driftedItems.length > 0;

  const calcModalRacikan = (ukuran) => {
    return (product.racikan || []).reduce((sum, r) => {
      if (!r.bahan_id || !r.persentase) return sum;
      const vol = (safeFloat(r.persentase) / 100) * ukuran;
      const hSatuan = (r.satuan || 'kg').toLowerCase();
      const hargaBeliSatuan = r.harga_per_satuan || 0;
      const perMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram' || hSatuan === 'ml') ? hargaBeliSatuan / 1000 : hargaBeliSatuan;
      return sum + perMl * vol;
    }, 0);
  };

  const modalRacikanLabel = calcModalRacikan(ukuranLabelMl);
  const modalRacikanAktual = hasAktual ? calcModalRacikan(ukuranAktualMl) : 0;
  let modalRacikan = (product.ukuran_digunakan === 'aktual' && hasAktual) ? modalRacikanAktual : modalRacikanLabel;

  const calcJualRacikan = (ukuran) => {
    return (product.racikan || []).reduce((sum, r) => {
      if (!r.bahan_id || !r.persentase) return sum;
      const vol = (safeFloat(r.persentase) / 100) * ukuran;
      const hargaBeliSatuan = r.harga_per_satuan || 0;
      const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : (hargaBeliSatuan * 1.3);
      const hSatuan = (r.satuan || 'kg').toLowerCase();
      
      let perMl;
      if (r.custom_harga_per_unit != null && r.custom_harga_per_unit !== 0) {
        perMl = r.custom_harga_per_unit;
      } else {
        perMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram' || hSatuan === 'ml') ? hJualSat / 1000 : hJualSat;
      }
      return sum + perMl * vol;
    }, 0);
  };
  
  let jualRacikan = calcJualRacikan((product.ukuran_digunakan === 'aktual' && hasAktual) ? ukuranAktualMl : ukuranLabelMl);

  const modalKomponen = (product.komponen_tambahan || []).reduce((sum, k) => sum + (k.harga || 0), 0);
  const jualKomponen = (product.komponen_tambahan || []).reduce((sum, k) => sum + (k.harga_jual || 0), 0);
  const otomatisJasa = hitungJasa(ukuranLabelMl);
  const jasaNominal = product.jasa_nominal !== undefined ? product.jasa_nominal : otomatisJasa;

  let modalTotalPerPcs = 0;
  let calculatedJualTotal = 0;
  let modalMurni = 0;
  
  // Harga Jual Default (Inventori)
  const jualBotolDef = botolDariClient ? 0 : getHargaJual(botol);
  const jualTutupDef = tutupDariClient ? 0 : getHargaJual(tutup);
  const jualSprayDef = sprayDariClient ? 0 : getHargaJual(spray);
  
  // Harga Jual Kemasan (manual input fallback)
  const jualBotol = product.botol_harga_jual !== undefined ? product.botol_harga_jual : jualBotolDef;
  const jualTutup = product.tutup_harga_jual !== undefined ? product.tutup_harga_jual : jualTutupDef;
  const jualSpray = product.spray_harga_jual !== undefined ? product.spray_harga_jual : jualSprayDef;
  
  let jualPackagingManual = product.is_fullset && fullset
    ? (product.fullset_harga_jual !== undefined ? product.fullset_harga_jual : getHargaJual(fullset))
    : (jualBotol + jualTutup + jualSpray);

  if (isDiscoverySet) {
    let subModalPack = 0;
    let subJualPack = 0;
    let subModalRacikan = 0;
    let subJualRacikan = 0;

    (product.items || []).forEach(it => {
      const iBotol = botolList.find(b => b.id === it.botol_id);
      const iTutup = tutupList.find(t => t.id === it.tutup_id);
      const iSpray = sprayList.find(s => s.id === it.spray_id);
      const iFullset = fullsetList?.find(f => f.id === it.fullset_id);
      
      if (iBotol && !it.botol_dari_client) {
        subModalPack += getHargaBeli(iBotol);
        subJualPack += getHargaJual(iBotol);
      }
      if (iTutup && !it.tutup_dari_client) {
        subModalPack += getHargaBeli(iTutup);
        subJualPack += getHargaJual(iTutup);
      }
      if (iSpray && !it.spray_dari_client) {
        subModalPack += getHargaBeli(iSpray);
        subJualPack += getHargaJual(iSpray);
      }
      if (iFullset) {
        subModalPack += getHargaBeli(iFullset);
        subJualPack += getHargaJual(iFullset);
      }
      
      const regexMatch = it.nama_variant?.match(/\((\d+)ml\)/);
      const fallbackMl = regexMatch ? parseFloat(regexMatch[1]) : (it.botol_ukuran_client || 0);
      const iUkuranLabel = iBotol?.ukuran_label_ml || fallbackMl;
      const iUkuranAktual = it.custom_ukuran_aktual_ml !== undefined ? it.custom_ukuran_aktual_ml : (iBotol?.ukuran_aktual_ml || fallbackMl);
      const iUkuranDipakai = it.custom_ukuran_aktual_ml !== undefined ? iUkuranAktual : iUkuranLabel;

      subModalRacikan += (it.racikan || []).reduce((sum, r) => {
        if (!r.bahan_id || !r.persentase) return sum;
        const vol = (safeFloat(r.persentase) / 100) * iUkuranDipakai;
        const hSatuan = (r.satuan || 'kg').toLowerCase();
        const hargaBeliSatuan = r.harga_per_satuan || 0;
        const perMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram' || hSatuan === 'ml') ? hargaBeliSatuan / 1000 : hargaBeliSatuan;
        return sum + perMl * vol;
      }, 0);

      subJualRacikan += (it.racikan || []).reduce((sum, r) => {
        const hSatuan = (r.satuan || 'kg').toLowerCase();
        const isLiter = hSatuan === 'g';
        const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : 0;
        const hJualKecil = r.custom_harga_per_unit || (hSatuan === 'gram' ? hJualSat : hJualSat / 1000);
        const vMl = r.volume_ml || ((safeFloat(r.persentase) / 100) * iUkuranDipakai);
        return sum + (hJualKecil * vMl);
      }, 0);
    });

    modalTotalPerPcs = subModalPack + subModalRacikan + modalKomponen + jasaNominal;
    calculatedJualTotal = subJualPack + subJualRacikan + jasaNominal + jualKomponen;
    modalMurni = subModalPack + subModalRacikan + modalKomponen;
    
    modalPackaging = subModalPack;
    jualPackagingManual = subJualPack;
    modalRacikan = subModalRacikan;
    jualRacikan = subJualRacikan;
  } else {
    modalTotalPerPcs = modalPackaging + modalRacikan + modalKomponen + jasaNominal;
    calculatedJualTotal = jualPackagingManual + jualRacikan + jasaNominal + jualKomponen;
    modalMurni = modalPackaging + modalRacikan + modalKomponen;
  }
  
  const hargaJualPerPcs = product.harga_jual_per_pcs !== undefined ? product.harga_jual_per_pcs : calculatedJualTotal;
  
  const finalHargaJualPerPcs = product.harga_jual_per_pcs !== undefined ? product.harga_jual_per_pcs : calculatedJualTotal;
  const finalJasaNominal = product.jasa_nominal !== undefined ? product.jasa_nominal : otomatisJasa;

  // Margin (Jasa = 100% masuk profit, bukan biaya produksi)
  const marginNominal = finalHargaJualPerPcs - modalMurni;
  const marginPersen = modalMurni > 0 && finalHargaJualPerPcs > 0 ? (marginNominal / modalMurni) * 100 : 0;
  
  // Rekomendasi ASA (Smart & Opinionated — Parfum Maklon Edition)
  const getAsaRecommendation = () => {
    const role = member?.role || 'default';
    const userName = member?.full_name ? member.full_name.split(' ')[0] : null;
    const isOwner = role === 'owner' || role === 'developer';
    const sapaan = isOwner ? (userName ? `Halo pak ${userName}!` : 'Halo pak!') : (userName ? `Halo kak ${userName}!` : 'Halo kak!');
    const qty = product.qty || 1;
    const namaVariant = (product.nama_product || '').toLowerCase();
    const ukMl = ukuranLabelMl || 0;
    let tgt;
    if (qty >= 10000) tgt = 12;
    else if (qty >= 5000) tgt = 18;
    else if (qty >= 3000) tgt = 25;
    else if (qty >= 2000) tgt = 30;
    else if (qty >= 1000) tgt = 40;
    else if (qty >= 500) tgt = 50;
    else if (qty >= 200) tgt = 60;
    else if (qty >= 100) tgt = 70;
    else if (qty >= 50) tgt = 80;
    else tgt = 100;
    if (namaVariant.includes('edp') || namaVariant.includes('eau de parfum') || namaVariant.includes('parfum')) tgt += 15;
    else if (namaVariant.includes('edt') || namaVariant.includes('eau de toilette')) tgt += 10;
    else if (namaVariant.includes('body mist') || namaVariant.includes('splash')) tgt += 5;
    else if (namaVariant.includes('refill') || namaVariant.includes('isi ulang')) tgt -= 10;
    if (ukMl >= 100) tgt += 5;
    else if (ukMl <= 15) tgt += 8;
    const asaPrice = Math.ceil((modalMurni * (1 + tgt / 100) + jasaNominal) / 500) * 500;
    const seed = Math.floor(modalMurni * 7 + qty * 3 + ukMl) % 10;
    const index = Math.abs(seed) % 3;
    let c = '', v = '';
    if (marginNominal < 0) {
      c = [`Ini minus Rp ${fmt(Math.abs(marginNominal))} per pcs, harus segera dinaikkin harganya ya.`, `Rugi Rp ${fmt(Math.abs(marginNominal))} per botol, ini nggak bisa dijual dengan harga segini.`, `Harga ini masih di bawah modal, rugi Rp ${fmt(Math.abs(marginNominal))}/pcs.`][index];
      v = `Asa rekomendasiin jual minimal di Rp ${fmt(asaPrice)} biar nggak rugi.`;
    } else if (marginPersen < 10) {
      c = [`Margin ${marginPersen.toFixed(1)}% ini terlalu tipis, hampir nggak ada untungnya.`, `${marginPersen.toFixed(1)}% doang? Ini masih terlalu mepet sama modal.`, `Margin segini tipis banget, perlu dinaikkin lagi harganya.`][index];
      v = `Asa saranin naikin ke Rp ${fmt(asaPrice)} biar ada ruang profit yang sehat.`;
    } else if (marginPersen < 30) {
      c = [`Margin ${marginPersen.toFixed(1)}% lumayan, tapi masih bisa lebih optimal.`, `${marginPersen.toFixed(1)}% oke buat ${qty >= 1000 ? 'volume segini' : 'batch ini'}, tapi masih ada ruang naikkin.`, `${marginPersen.toFixed(1)}% cukup buat jalan, tapi belum ideal.`][index];
      v = qty >= 1000 ? `Volume ${fmt(qty)} pcs masih wajar. Sweet-spot menurut Asa di Rp ${fmt(asaPrice)}.` : `Menurut Asa, jual di Rp ${fmt(asaPrice)} lebih optimal.`;
    } else if (marginPersen < 80) {
      c = [`Margin ${marginPersen.toFixed(1)}%, udah bagus dan sehat buat bisnis maklon.`, `${marginPersen.toFixed(1)}% — solid! Harga dan margin-nya udah seimbang.`, `Margin ${marginPersen.toFixed(1)}% ini udah bikin cashflow sehat, Asa suka.`][index];
      v = qty >= 500 ? `Untuk ${fmt(qty)} pcs, ini udah cuan dan kompetitif. Asa approve!` : `Harga sehat, margin bagus. Asa kasih jempol!`;
    } else if (marginPersen < 150) {
      c = [`Margin ${marginPersen.toFixed(1)}%, ini premium banget tapi masih masuk akal.`, `${marginPersen.toFixed(1)}% — harga premium, pastiin value produknya juga sepadan ya.`, `Margin ${marginPersen.toFixed(1)}%, termasuk tinggi. Pastikan kualitas produk mendukung harga ini.`][index];
      v = `Pastiin client ngerasa puas sama kualitasnya biar repeat order.`;
    } else {
      c = [`Margin ${marginPersen.toFixed(1)}%, ini sangat tinggi. Pastiin harga ini realistis untuk marketnya ya.`, `${marginPersen.toFixed(1)}% — wow, ini super premium. Cek lagi apakah client nyaman di harga segini.`, `Margin segini luar biasa tinggi, ${marginPersen.toFixed(1)}%. Perlu dipastikan client memang target market premium.`][index];
      v = `Pastiin client happy dan ngerasa worth it sama produknya.`;
    }
    if (isOwner) return `${sapaan} ${c} ${v}`;
    if (role === 'finance') return `${sapaan} ${c} HPP murni Rp ${fmt(modalMurni)}, jasa Rp ${fmt(jasaNominal)}. ${v}`;
    if (role === 'marketing') return `${sapaan} ${c} ${v} Semangat closing ya kak!`;
    return `${sapaan} ${c} ${v}`;
  };

  useEffect(() => {
    const qty = product.qty || 1;
    const newTotalHargaJual = finalHargaJualPerPcs * qty;
    const newTotalHpp = modalTotalPerPcs * qty;
    
    onChange({
      final_jasa_nominal: finalJasaNominal,
      final_harga_jual_per_pcs: finalHargaJualPerPcs,
      total_harga_jual: newTotalHargaJual,
      total_hpp_product: newTotalHpp,
      hpp_per_pcs_digunakan: modalTotalPerPcs,
      botol_harga_beli: modalBotol,
      tutup_harga_beli: modalTutup,
      spray_harga_beli: modalSpray,
      fullset_harga_beli: product.is_fullset && fullset ? fullset.harga_rupiah : 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalHargaJualPerPcs, finalJasaNominal, modalTotalPerPcs, product.qty, modalBotol, modalTutup, modalSpray]);

  const addRacikan = () => {
    onChange({ racikan: [...(product.racikan || []), { kategori: 'Bibit', vendor: '', bahan_id: null, bahan_nama: '', persentase: 0, volume_ml: 0, hpp_bahan: 0, harga_per_satuan: 0, satuan: 'kg' }] });
  };

  const updateRacikan = useCallback((idx, data) => {
    const updated = [...(product.racikan || [])];
    updated[idx] = { ...updated[idx], ...data };
    onChange({ racikan: updated });
  }, [product.racikan, onChange]);

  const removeRacikan = (idx) => {
    onChange({ racikan: (product.racikan || []).filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white border border-[#E6D5B8] rounded-2xl overflow-hidden shadow-sm mb-4">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 bg-[#F9F5EC] border-b border-[#E6D5B8]">
        <div className="flex items-center gap-2 sm:gap-3 flex-grow">
          <div className="w-8 h-8 shrink-0 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-bold text-sm shadow-sm">{index + 1}</div>
          <div className="flex-grow w-full min-w-0">
            <Input
              value={product.nama_product || ''}
              onChange={e => onChange({ nama_product: e.target.value })}
              placeholder="Contoh: Sun Zest Serum"
              className="font-bold text-[#302621] text-sm sm:text-base border-0 bg-transparent p-0 h-auto focus-visible:ring-0 w-full placeholder:text-[#A39171]"
            />
            {isValid && (
              <div className="flex items-center gap-1 mt-0.5">
                <CheckCircle2 className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-600 font-medium">Formula Lengkap (100%)</span>
                {isDiscoverySet && (
                  <Badge variant="outline" className="ml-2 text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                    <Info className="w-3 h-3 mr-1" />
                    MODE DISCOVERY SET
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isDatabaseMode && onDuplicate && (
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={onDuplicate} 
              className="h-8 border-[#D4AF37]/50 bg-[#FDF8EE] hover:bg-[#F4EDE1] text-[#8C6208] text-xs font-bold px-2.5 flex items-center gap-1.5 rounded-lg shadow-2xs transition-colors cursor-pointer"
              title="Duplikat spesifikasi kemasan ini untuk varian baru"
            >
              <Copy className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Duplikat Kemasan</span>
            </Button>
          )}
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs">Hapus</Button>
          )}
          <Button variant="ghost" size="icon" className="w-8 h-8 text-[#8C6208] hover:bg-[#E6D5B8]/50" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="p-6 space-y-6">
          {/* Drift Detection Alert Banner */}
          {hasDrift && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-start gap-3 shadow-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong className="font-bold block mb-1">Perhatian: Data Master Telah Berubah!</strong>
                Harga pada invoice ini dikunci agar tidak berubah secara otomatis (Snapshot Invoice), namun data master di inventori saat ini berbeda dengan saat invoice dibuat:
                <ul className="list-disc pl-4 mt-2 space-y-1">
                  {driftedItems.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Info Kuantitas + Ukuran Botol */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-[#7A695B] uppercase tracking-wider mb-1 block">Kuantitas Pesanan (Pcs)</label>
              <Input type="number" min="1" value={product.qty === '' ? '' : (product.qty || '')} onChange={e => onChange({ qty: e.target.value === '' ? '' : parseInt(e.target.value) })} className="h-10 border-[#E6D5B8] focus-visible:ring-[#D4AF37] rounded-xl" />
            </div>
            
            {!isDiscoverySet && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="text-xs font-bold text-[#7A695B] uppercase tracking-wider mb-1 block">Ukuran Label (ml)</label>
                  {(botolDariClient || (product.is_fullset && !botol)) ? (
                    <Input type="number" min="0" placeholder="Contoh: 30" value={product.botol_ukuran_client || ''} onChange={e => onChange({ botol_ukuran_client: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) })} className="h-10 border-[#E6D5B8] focus-visible:ring-[#D4AF37] rounded-xl" />
                  ) : (
                    <div className="h-10 flex items-center px-3 border border-[#E6D5B8] rounded-xl bg-[#F9F5EC] text-sm font-semibold text-[#302621]">
                      {ukuranLabelMl > 0 ? `${ukuranLabelMl} ml` : 'Pilih botol'}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block">Isi Aktual (ml)</label>
                  <Input 
                    type="number" 
                    min="0" 
                    placeholder={ukuranLabelMl > 0 ? `${ukuranLabelMl}` : "0"} 
                    value={product.custom_ukuran_aktual_ml !== undefined ? product.custom_ukuran_aktual_ml : (ukuranAktualMl > 0 ? ukuranAktualMl : '')} 
                    onChange={e => {
                      const val = e.target.value;
                      onChange({ 
                        custom_ukuran_aktual_ml: val === '' ? undefined : parseFloat(val),
                        ukuran_digunakan: val !== '' ? 'aktual' : 'label'
                      });
                    }} 
                    className="h-10 border-blue-200 text-blue-700 bg-blue-50 focus:ring-blue-500 font-bold rounded-xl" 
                  />
                </div>
              </div>
            )}
          </div>

          {/* Pemilihan Kemasan (Packaging) */}
          {!isDiscoverySet && (
            <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-[#302621] flex items-center gap-2">
                <div className="w-6 h-6 rounded-xl bg-[#F9F5EC] flex items-center justify-center">
                  <Package strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" />
                </div>
                Kemasan & Packaging (Harga Beli/Modal Tetap)
              </h4>
              <div className="flex bg-slate-100 p-1 rounded-xl">
                <button 
                  onClick={() => onChange({ is_fullset: false, fullset_id: null })}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${!product.is_fullset ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Satuan
                </button>
                <button 
                  onClick={() => onChange({ is_fullset: true, botol_dari_client: false, tutup_dari_client: false, spray_dari_client: false })}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${product.is_fullset ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Fullset Gabungan
                </button>
              </div>
            </div>

            {product.is_fullset && (
              <div className="mb-4 bg-emerald-50/50 border border-emerald-100 rounded-xl p-4">
                <label className="text-xs font-semibold text-emerald-800 mb-1.5 block">Pilih Data Fullset</label>
                <SearchableSelect
                  options={fullsetList}
                  value={product.fullset_id}
                  onChange={v => {
                    const f = fullsetList.find(x => x.id === v);
                    let detectedLabel = 0;
                    let detectedAktual = 0;
                    if (f) {
                      detectedLabel = parseFloat(f.ukuran_label) || 0;
                      detectedAktual = parseFloat(f.ukuran_aktual) || 0;
                      if (!detectedLabel && f.nama) {
                        const match = f.nama.match(/(\d+)\s*(?:ml|g|gr|gram)/i);
                        if (match) detectedLabel = parseFloat(match[1]);
                      }
                    }
                    onChange({ 
                      fullset_id: v, 
                      botol_id: f?.botol_id || null,
                      tutup_id: f?.tutup_id || null,
                      spray_id: f?.spray_id || null,
                      botol_ukuran_client: detectedLabel || product.botol_ukuran_client || 0,
                      custom_ukuran_aktual_ml: detectedAktual || detectedLabel || product.custom_ukuran_aktual_ml || undefined,
                      ukuran_digunakan: (detectedAktual || detectedLabel || product.custom_ukuran_aktual_ml) ? 'aktual' : 'label'
                    });
                  }}
                  placeholder="Cari fullset..."
                  displayKey="nama"
                  valueKey="id"
                  renderOption={(opt) => (
                    <div>
                      <div className="font-medium text-[#302621]">{opt.nama}</div>
                      <div className="text-xs text-[#7A695B]">Beli: Rp {fmt(opt.harga_rupiah)} | Jual: Rp {fmt(opt.harga_jual)}</div>
                    </div>
                  )}
                />
                
                {fullset && (
                  <div className="bg-white border border-emerald-200 rounded-xl p-3 space-y-3 mt-3">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-medium">Harga Modal (Beli) Fullset:</span>
                      <span className="font-bold text-slate-800">Rp {fmt(fullset.harga_rupiah)}</span>
                    </div>
                    <div className="border-t border-slate-100 pt-3">
                      <InlinePriceEditor 
                        label="Harga Jual Fullset:"
                        value={product.fullset_harga_jual !== undefined ? product.fullset_harga_jual : fullset.harga_jual}
                        onSave={(val) => onChange({ fullset_harga_jual: val })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border ${product.is_fullset ? 'border-slate-100 bg-slate-50/30 opacity-75' : 'border-[#E6D5B8]/50 bg-slate-50/50'}`}>
              {/* Botol */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#4A3B32]">Botol / Wadah</label>
                  <div className="flex bg-slate-100 p-0.5 rounded-lg">
                    <button type="button" onClick={() => { setTabBotol('satala'); onChange({ botol_id: null, botol_dari_client: false, botol_nama_client: '' }); }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${tabBotol === 'satala' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500'}`}>Satala</button>
                    <button type="button" onClick={() => { setTabBotol('client'); onChange({ botol_id: null, botol_dari_client: true, botol_nama_client: 'Botol Dari Client' }); }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${tabBotol === 'client' ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500'}`}>Dari Client</button>
                  </div>
                </div>
                
                {tabBotol === 'client' ? (
                  <div className="space-y-3 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-3 shadow-sm">
                    <div>
                      <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                      <SearchableSelect
                        options={botolList.filter(b => b.dari_client)}
                        value={product.botol_id}
                        onChange={v => onChange({ botol_id: v, botol_dari_client: false, botol_nama_client: '' })}
                        placeholder="Pilih dari inventori..."
                        displayKey="nama"
                        valueKey="id"
                        disabled={product.is_fullset}
                        renderOption={(opt) => (
                          <div>
                            <div className="font-semibold text-[#302621]">{opt.nama}</div>
                            <div className="text-xs text-[#7A695B] mt-0.5">{opt.ukuran_label_ml}ml • Rp {fmt(opt.harga_rupiah)}</div>
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                      <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                    </div>
                    
                    <div>
                      <Input 
                        placeholder="Ketik nama botol baru..." 
                        value={product.botol_nama_client || ''}
                        onChange={e => onChange({ botol_nama_client: e.target.value, botol_dari_client: !!e.target.value, botol_id: null })}
                        disabled={product.is_fullset}
                        className="h-8 text-xs border-emerald-200 rounded-xl"
                      />
                      {product.botol_dari_client && <span className="text-[9px] text-emerald-600 font-bold mt-1 block">Tercatat sebagai: Item dari Client (Modal: Rp 0)</span>}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <SearchableSelect
                      options={botolList.filter(b => !b.dari_client)}
                      value={product.botol_id}
                      onChange={v => onChange({ botol_id: v, botol_dari_client: false })}
                      placeholder="Cari botol inventori Satala..."
                      displayKey="nama"
                      valueKey="id"
                      disabled={product.is_fullset}
                      renderOption={(opt) => (
                        <div>
                          <div className="font-semibold text-[#302621]">{opt.nama}</div>
                          <div className="text-xs text-[#7A695B] mt-0.5">{opt.ukuran_label_ml}ml • Rp {fmt(opt.harga_rupiah)}</div>
                        </div>
                      )}
                    />
                    {botol && !botol.dari_client && (
                      <div className="bg-white border border-slate-200 rounded-xl p-2 space-y-2">
                        <p className="text-[10px] font-medium text-slate-500">Harga Beli: <span className="font-bold text-slate-800">Rp {fmt(modalBotol)}</span></p>
                        <div className="flex items-center gap-1.5 mt-2">
                          <InlinePriceEditor 
                            label="Harga Jual:"
                            value={jualBotol}
                            onSave={(val) => onChange({ botol_harga_jual: val })}
                          />
                        </div>
                        {(product.botol_harga_jual !== undefined && product.botol_harga_jual < modalBotol) && (
                          <p className="text-[9px] text-red-500 flex items-start gap-1 font-medium leading-tight">
                            <AlertCircle className="w-3 h-3 shrink-0" /> Harga jual di bawah modal/beli.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tutup */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#4A3B32]">Tutup (Cap)</label>
                  <div className="flex bg-slate-100 p-0.5 rounded-lg">
                    <button type="button" onClick={() => { setTabTutup('satala'); onChange({ tutup_id: null, tutup_dari_client: false, tutup_nama_client: '' }); }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${tabTutup === 'satala' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500'}`}>Satala</button>
                    <button type="button" onClick={() => { setTabTutup('client'); onChange({ tutup_id: null, tutup_dari_client: true, tutup_nama_client: 'Tutup Dari Client' }); }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${tabTutup === 'client' ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500'}`}>Dari Client</button>
                  </div>
                </div>
                
                {tabTutup === 'client' ? (
                  <div className="space-y-3 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-3 shadow-sm">
                    <div>
                      <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                      <SearchableSelect
                        options={tutupList.filter(t => t.dari_client)}
                        value={product.tutup_id}
                        onChange={v => onChange({ tutup_id: v, tutup_dari_client: false, tutup_nama_client: '' })}
                        placeholder="Pilih dari inventori..."
                        displayKey="nama"
                        valueKey="id"
                        disabled={product.is_fullset}
                        renderOption={(opt) => (
                          <div>
                            <div className="font-semibold text-[#302621]">{opt.nama}</div>
                            <div className="text-xs text-[#7A695B] mt-0.5">Rp {fmt(opt.harga_rupiah)}</div>
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                      <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                    </div>
                    
                    <div>
                      <Input 
                        placeholder="Ketik nama tutup baru..." 
                        value={product.tutup_nama_client || ''}
                        onChange={e => onChange({ tutup_nama_client: e.target.value, tutup_dari_client: !!e.target.value, tutup_id: null })}
                        disabled={product.is_fullset}
                        className="h-8 text-xs border-emerald-200 rounded-xl"
                      />
                      {product.tutup_dari_client && <span className="text-[9px] text-emerald-600 font-bold mt-1 block">Tercatat sebagai: Item dari Client (Modal: Rp 0)</span>}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <SearchableSelect
                      options={tutupList.filter(t => !t.dari_client)}
                      value={product.tutup_id}
                      onChange={v => onChange({ tutup_id: v, tutup_dari_client: false })}
                      placeholder="Cari tutup inventori Satala..."
                      displayKey="nama"
                      valueKey="id"
                      disabled={product.is_fullset}
                      renderOption={(opt) => (
                        <div>
                          <div className="font-semibold text-[#302621]">{opt.nama}</div>
                          <div className="text-xs text-[#7A695B] mt-0.5">Rp {fmt(opt.harga_rupiah)}</div>
                        </div>
                      )}
                    />
                    {tutup && !tutup.dari_client && (
                      <div className="bg-white border border-slate-200 rounded-xl p-2 space-y-2">
                        <p className="text-[10px] font-medium text-slate-500">Harga Beli: <span className="font-bold text-slate-800">Rp {fmt(modalTutup)}</span></p>
                        <div className="flex items-center gap-1.5 mt-2">
                          <InlinePriceEditor 
                            label="Harga Jual:"
                            value={jualTutup}
                            onSave={(val) => onChange({ tutup_harga_jual: val })}
                          />
                        </div>
                        {(product.tutup_harga_jual !== undefined && product.tutup_harga_jual < modalTutup) && (
                          <p className="text-[9px] text-red-500 flex items-start gap-1 font-medium leading-tight">
                            <AlertCircle className="w-3 h-3 shrink-0" /> Harga jual di bawah modal/beli.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Spray */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#4A3B32]">Spray / Pump</label>
                  <div className="flex bg-slate-100 p-0.5 rounded-lg">
                    <button type="button" onClick={() => { setTabSpray('satala'); onChange({ spray_id: null, spray_dari_client: false, spray_nama_client: '' }); }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${tabSpray === 'satala' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500'}`}>Satala</button>
                    <button type="button" onClick={() => { setTabSpray('client'); onChange({ spray_id: null, spray_dari_client: true, spray_nama_client: 'Spray Dari Client' }); }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${tabSpray === 'client' ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500'}`}>Dari Client</button>
                  </div>
                </div>
                
                {tabSpray === 'client' ? (
                  <div className="space-y-3 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-3 shadow-sm">
                    <div>
                      <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                      <SearchableSelect
                        options={sprayList.filter(s => s.dari_client)}
                        value={product.spray_id}
                        onChange={v => onChange({ spray_id: v, spray_dari_client: false, spray_nama_client: '' })}
                        placeholder="Pilih dari inventori..."
                        displayKey="nama"
                        valueKey="id"
                        disabled={product.is_fullset}
                        renderOption={(opt) => (
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-[#302621]">{opt.nama}</span>
                              {opt.tipe && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${opt.tipe === 'Continuous' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{opt.tipe}</span>}
                            </div>
                            <div className="text-xs text-[#7A695B] mt-0.5">Rp {fmt(opt.harga_rupiah)}</div>
                          </div>
                        )}
                        renderSelected={(opt) => (
                          <span className="flex items-center gap-1.5">
                            {opt.nama}
                            {opt.tipe && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${opt.tipe === 'Continuous' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{opt.tipe}</span>}
                          </span>
                        )}
                      />
                    </div>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                      <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                    </div>
                    
                    <div>
                      <Input 
                        placeholder="Ketik nama spray baru..." 
                        value={product.spray_nama_client || ''}
                        onChange={e => onChange({ spray_nama_client: e.target.value, spray_dari_client: !!e.target.value, spray_id: null })}
                        disabled={product.is_fullset}
                        className="h-8 text-xs border-emerald-200 rounded-xl"
                      />
                      {product.spray_dari_client && <span className="text-[9px] text-emerald-600 font-bold mt-1 block">Tercatat sebagai: Item dari Client (Modal: Rp 0)</span>}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <SearchableSelect
                      options={sprayList.filter(s => !s.dari_client)}
                      value={product.spray_id}
                      onChange={v => onChange({ spray_id: v, spray_dari_client: false })}
                      placeholder="Cari spray inventori Satala..."
                      displayKey="nama"
                      valueKey="id"
                      disabled={product.is_fullset}
                      renderOption={(opt) => (
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#302621]">{opt.nama}</span>
                            {opt.tipe && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${opt.tipe === 'Continuous' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{opt.tipe}</span>}
                          </div>
                          <div className="text-xs text-[#7A695B] mt-0.5">Rp {fmt(opt.harga_rupiah)}</div>
                        </div>
                      )}
                      renderSelected={(opt) => (
                        <span className="flex items-center gap-1.5">
                          {opt.nama}
                          {opt.tipe && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${opt.tipe === 'Continuous' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{opt.tipe}</span>}
                        </span>
                      )}
                    />
                    {spray && !spray.dari_client && (
                      <div className="bg-white border border-slate-200 rounded-xl p-2 space-y-2">
                        <p className="text-[10px] font-medium text-slate-500">Harga Beli: <span className="font-bold text-slate-800">Rp {fmt(modalSpray)}</span></p>
                        <div className="flex items-center gap-1.5 mt-2">
                          <InlinePriceEditor 
                            label="Harga Jual:"
                            value={jualSpray}
                            onSave={(val) => onChange({ spray_harga_jual: val })}
                          />
                        </div>
                        {(product.spray_harga_jual !== undefined && product.spray_harga_jual < modalSpray) && (
                          <p className="text-[9px] text-red-500 flex items-start gap-1 font-medium leading-tight">
                            <AlertCircle className="w-3 h-3 shrink-0" /> Harga jual di bawah modal/beli.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Formulary Racikan */}
          {!isDiscoverySet && (
            <div>
            <div className="flex flex-row items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-6 h-6 rounded-xl bg-[#F9F5EC] flex items-center justify-center shrink-0">
                    <FlaskConical strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" />
                  </div>
                  <h4 className="text-xs md:text-sm font-bold text-[#302621] flex items-center gap-2 flex-wrap truncate">
                    Komposisi Cairan
                    {ukuranLabel > 0 && <Badge variant="outline" className="text-[10px] bg-white text-[#7A695B] shrink-0">Acuan: {ukuranLabel}ml</Badge>}
                  </h4>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full ${isValid ? 'bg-green-100 text-green-700' : totalPersen > 100 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {totalPersen.toFixed(1)}% / 100%
                  </span>
                  <Button size="sm" onClick={addRacikan} className="h-8 text-xs gap-1 bg-[#302621] text-white hover:bg-[#1A1411] rounded-xl px-3">
                    <Plus className="w-3 h-3" /> Tambah Cairan
                  </Button>
                </div>
            </div>

            {!ukuranLabelMl && (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs mb-3">
                <AlertCircle className="w-4 h-4 shrink-0" />
                Pilih botol terlebih dahulu agar kami bisa menghitung takaran volume otomatis.
              </div>
            )}

            {(product.racikan || []).length > 0 && (
              <div className="bg-[#F9F5EC]/50 border border-[#E6D5B8] rounded-xl px-3 py-2 space-y-1">
                {(product.racikan || []).map((row, i) => (
                  <RacikanRow
                    key={i}
                    row={row}
                    index={i}
                    bahanList={bahanList}
                    onUpdate={updateRacikan}
                    onRemove={removeRacikan}
                    ukuranMl={ukuranLabel}
                  />
                ))}
              </div>
            )}
          </div>
          )}

          {/* Variasi Discovery Set */}
          {isDiscoverySet && (
            <div className="mb-4">
              <DiscoverySetItems 
                items={product.items || []} 
                onChange={(newItems) => onChange({ items: newItems })}
                botolList={botolList}
                tutupList={tutupList}
                sprayList={sprayList}
                bahanList={bahanList}
                fullsetList={fullsetList}
                isMarketing={true}
                isDatabaseMode={isDatabaseMode}
              />
            </div>
          )}

          {/* Komponen Tambahan (Box, Sticker, dll) */}
          <div className="border-t border-[#E6D5B8] pt-6">
            {isDatabaseMode && (
              <div className="mb-3">
                <h4 className="text-sm font-bold flex items-center gap-2 text-[#302621] mb-3">
                  <div className="w-6 h-6 rounded-xl bg-[#F9F5EC] flex items-center justify-center">
                    <Tag strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" />
                  </div>
                  Item Tambahan Kemasan
                </h4>
                {(!product.komponen_tambahan || product.komponen_tambahan.length === 0) ? (
                  <div className="text-center py-4 text-xs text-[#7A695B]">Tidak ada kelengkapan tambahan.</div>
                ) : (
                  <div className="space-y-2">
                    {product.komponen_tambahan.map((k, idx) => {
                      const priceModal = (k.harga || 0);
                      const priceJual = (k.harga_jual || 0);
                      const qty = k.qty !== undefined && k.qty !== null && k.qty !== 1 ? k.qty : (isDiscoverySet ? 1 : (k.qty || 1));
                      return (
                      <div key={idx} className="bg-white border border-[#E6D5B8]/70 rounded-lg p-3 flex justify-between items-center shadow-sm">
                         <div>
                           <div className="font-bold text-[#302621] text-sm flex items-center gap-2">
                             {k.item_nama || k.nama_item || k.nama} {qty > 1 ? `(${qty} pcs)` : ''}
                             <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">
                               {k.kategori}
                             </span>
                             {k.dari_client && (
                               <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">
                                 Dari Client
                               </span>
                             )}
                           </div>
                           {k.catatan && <div className="text-[11px] text-[#7A695B] mt-0.5 italic">"{k.catatan}"</div>}
                         </div>
                         <div className="text-right">
                           {!k.dari_client ? (
                             <>
                               <div className="text-xs font-black text-[#8C6208]">Rp {fmt(priceJual)} / {k.satuan || 'pcs'}</div>
                               <div className="text-[10px] text-[#7A695B] font-medium mt-0.5">Modal: Rp {fmt(priceModal)}</div>
                             </>
                           ) : (
                             <div className="text-xs font-black text-emerald-600">Rp 0</div>
                           )}
                         </div>
                      </div>
                    )})}
                  </div>
                )}
              </div>
            )}

            {!isDatabaseMode && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold flex items-center gap-2 text-[#302621]">
                    <div className="w-6 h-6 rounded-xl bg-[#F9F5EC] flex items-center justify-center">
                      <Tag strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" />
                    </div>
                    Kelengkapan Tambahan (Harga Jual Bisa Diedit)
                  </h4>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onChange({ komponen_tambahan: [...(product.komponen_tambahan || []), { nama: '', kategori: 'Box', harga: 0, harga_jual: 0, dari_client: false, tab: 'satala' }] })} className="h-8 text-xs gap-1 border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl">
                      <Plus className="w-3 h-3" /> Tambah Kelengkapan
                    </Button>
                  </div>
                </div>
                
                <div className="bg-slate-50/50 border border-[#E6D5B8]/50 rounded-xl p-4">
                  {(!product.komponen_tambahan || product.komponen_tambahan.length === 0) ? (
                    <div className="text-center py-4 text-xs text-[#7A695B]">Tidak ada kelengkapan tambahan.</div>
                  ) : (
                    <div className="space-y-2">
                      {product.komponen_tambahan.map((komponen, i) => (
                        <div key={i} className="relative bg-white border border-[#E6D5B8] rounded-xl p-3 pr-10 space-y-2">
                          <button onClick={() => {
                            const updated = [...product.komponen_tambahan];
                            updated.splice(i, 1);
                            onChange({ komponen_tambahan: updated });
                          }} className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>

                          {/* Row 1: Tab + Item Selector */}
                          <div className="flex items-start gap-2">
                            <div className="flex bg-slate-100 p-0.5 rounded-lg shrink-0">
                              <button type="button" onClick={() => {
                                const updated = [...product.komponen_tambahan];
                                updated[i].tab = 'satala';
                                updated[i].dari_client = false;
                                updated[i].inventory_item_id = null;
                                onChange({ komponen_tambahan: updated });
                              }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${(komponen.tab !== 'client') ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500'}`}>Satala</button>
                              <button type="button" onClick={() => {
                                const updated = [...product.komponen_tambahan];
                                updated[i].tab = 'client';
                                updated[i].dari_client = true;
                                updated[i].nama = `${updated[i].kategori} Dari Client`;
                                updated[i].inventory_item_id = null;
                                onChange({ komponen_tambahan: updated });
                              }} className={`text-[9px] font-bold px-2 py-1 rounded-md transition-all ${(komponen.tab === 'client') ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500'}`}>Dari Client</button>
                            </div>

                            <div className="flex-1 min-w-0">
                              {komponen.tab === 'client' ? (
                                <div className="space-y-2 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-2 shadow-sm">
                                  <div className="flex gap-2 items-center">
                                    <label className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider shrink-0 w-16">Kategori:</label>
                                    <select value={komponen.kategori || 'Box'} onChange={e => {
                                      const updated = [...product.komponen_tambahan];
                                      updated[i].kategori = e.target.value;
                                      updated[i].inventory_item_id = null;
                                      updated[i].nama = '';
                                      onChange({ komponen_tambahan: updated });
                                    }} className="h-8 text-xs border border-emerald-200 rounded-xl px-2 bg-white outline-none flex-1 font-semibold text-emerald-900">
                                      <option value="Box">Box</option>
                                      <option value="Stiker">Stiker</option>
                                      <option value="Lainnya">Lainnya</option>
                                    </select>
                                  </div>
                                  <SearchableSelect
                                    options={itemTambahanList.filter(x => x.dari_client && x.kategori === (komponen.kategori || 'Box'))}
                                    value={komponen.inventory_item_id}
                                    onChange={v => {
                                      const item = itemTambahanList.find(x => x.id === v);
                                      const updated = [...product.komponen_tambahan];
                                      updated[i].inventory_item_id = v;
                                      updated[i].nama = item?.nama || '';
                                      updated[i].kategori = item?.kategori || komponen.kategori || 'Box';
                                      updated[i].harga = item?.harga_rupiah || 0;
                                      updated[i].dari_client = false;
                                      onChange({ komponen_tambahan: updated });
                                    }}
                                    placeholder={`Pilih ${komponen.kategori || 'Box'} dari inventori...`}
                                    displayKey="nama"
                                    valueKey="id"
                                    renderOption={(opt) => (
                                      <div>
                                        <div className="font-semibold text-[#302621]">{opt.nama}</div>
                                        <div className="text-xs text-[#7A695B] mt-0.5">Rp {fmt(opt.harga_rupiah)}</div>
                                      </div>
                                    )}
                                  />
                                  <div className="relative">
                                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                                    <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                                  </div>
                                  <div className="flex gap-2 items-center">
                                    <Input value={komponen.nama} onChange={e => {
                                      const updated = [...product.komponen_tambahan];
                                      updated[i].nama = e.target.value;
                                      updated[i].dari_client = !!e.target.value;
                                      updated[i].inventory_item_id = null;
                                      // kategori is already set by the dropdown at the top
                                      onChange({ komponen_tambahan: updated });
                                    }} placeholder={`Ketik nama ${komponen.kategori || 'Box'} baru...`} className="h-8 text-xs border-emerald-200 rounded-xl flex-1" />
                                  </div>
                                </div>
                              ) : (
                                <div className="flex gap-2 items-start">
                                  <select 
                                    value={komponen.filter_kategori || 'Semua'}
                                    onChange={e => {
                                      const updated = [...product.komponen_tambahan];
                                      updated[i].filter_kategori = e.target.value;
                                      onChange({ komponen_tambahan: updated });
                                    }}
                                    className="h-9 text-xs border border-[#E6D5B8] rounded-xl px-2 bg-white outline-none shrink-0 w-28"
                                  >
                                    <option value="Semua">Semua</option>
                                    {Array.from(new Set(itemTambahanList.filter(x => !x.dari_client && x.kategori).map(x => x.kategori))).sort().map(kat => (
                                      <option key={kat} value={kat}>{kat}</option>
                                    ))}
                                  </select>
                                  <div className="flex-1 min-w-0">
                                    <SearchableSelect
                                      options={itemTambahanList.filter(x => !x.dari_client && (!komponen.filter_kategori || komponen.filter_kategori === 'Semua' || x.kategori === komponen.filter_kategori))}
                                      value={komponen.inventory_item_id}
                                      onChange={v => {
                                        const item = itemTambahanList.find(x => x.id === v);
                                        const updated = [...product.komponen_tambahan];
                                        updated[i].inventory_item_id = v;
                                        updated[i].nama = item?.nama || '';
                                        updated[i].kategori = item?.kategori || 'Lainnya';
                                        updated[i].harga = item?.harga_rupiah || 0;
                                        updated[i].harga_jual = item?.harga_jual || 0;
                                        updated[i].dari_client = false;
                                        onChange({ komponen_tambahan: updated });
                                      }}
                                      placeholder="Cari item tambahan..."
                                      displayKey="nama"
                                      valueKey="id"
                                      renderOption={(opt) => (
                                        <div>
                                          <div className="font-semibold text-[#302621]">{opt.nama}</div>
                                          <div className="text-xs text-[#7A695B] mt-0.5">Modal: Rp {fmt(opt.harga_rupiah)} | Jual: Rp {fmt(opt.harga_jual || 0)}</div>
                                        </div>
                                      )}
                                    />
                                    {!komponen.inventory_item_id && <p className="text-[10px] text-amber-600 mt-0.5">Pilih item dari inventori Satala.</p>}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Row 2: Harga Modal + Harga Jual + Qty */}
                          <div className="flex items-center gap-3 pl-0.5">
                            <div className="w-24 shrink-0">
                              <label className="text-[10px] text-slate-500 mb-0.5 block">Qty / Pcs</label>
                              <Input type="number" min="1" value={komponen.qty !== undefined ? komponen.qty : 1} onChange={e => {
                                const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                                const updated = [...product.komponen_tambahan];
                                updated[i].qty = val > 0 ? val : 1;
                                onChange({ komponen_tambahan: updated });
                              }} className="h-8 text-xs bg-slate-50" />
                            </div>
                            <div className="w-28 shrink-0">
                              <label className="text-[10px] text-slate-500 mb-0.5 block">Modal / Beli</label>
                              <Input type="number" value={komponen.harga === 0 ? '' : komponen.harga} onChange={e => {
                                const updated = [...product.komponen_tambahan];
                                updated[i].harga = e.target.value === '' ? '' : (parseFloat(e.target.value) || 0);
                                onChange({ komponen_tambahan: updated });
                              }} className="h-8 text-xs bg-slate-50" placeholder="Rp 0" />
                            </div>
                            <div className="shrink-0">
                              <InlinePriceEditor 
                                label="Harga Jual"
                                value={komponen.harga_jual || 0}
                                onSave={val => {
                                  const updated = [...product.komponen_tambahan];
                                  updated[i].harga_jual = val;
                                  onChange({ komponen_tambahan: updated });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Setup Jasa & Harga Jual Akhir */}
          {(isValid && (ukuranLabel > 0 || isDiscoverySet)) && (
            <div className="bg-gradient-to-br from-[#302621] to-[#1A1411] text-[#FFFDF9] rounded-xl p-6 shadow-xl relative overflow-hidden mt-6">
              <h3 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4 border-b border-white/10 pb-3">
                Penentuan Harga Jual (Invoice)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 relative z-10">
                <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                  <label className="text-xs text-[#D4AF37] font-bold mb-1.5 block">Biaya Jasa Produksi / pcs</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/70">Rp</span>
                    <DecimalInput 
                      value={product.jasa_nominal}
                      onChange={val => onChange({ jasa_nominal: val })}
                      className="h-10 bg-transparent border-white/30 text-white placeholder:text-white/30 focus-visible:ring-[#D4AF37]" 
                      placeholder={`Default: Rp ${fmt(otomatisJasa)}`}
                    />
                  </div>
                  <p className="text-[10px] text-white/50 mt-1.5">Jasa dihitung sepenuhnya sebagai Margin Profit Internal.</p>
                </div>
                
                <div className="bg-[#D4AF37]/10 p-4 rounded-xl border border-[#D4AF37]/30">
                  <label className="text-xs text-[#D4AF37] font-bold mb-1.5 block">Harga Jual Akhir / pcs</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#D4AF37]">Rp</span>
                    <DecimalInput 
                      value={product.harga_jual_per_pcs}
                      onChange={val => onChange({ harga_jual_per_pcs: val })}
                      className="h-10 bg-white/5 border-[#D4AF37]/50 text-white font-bold placeholder:text-white/30 focus-visible:ring-[#D4AF37]" 
                      placeholder={`Rekomendasi min: Rp ${fmt(calculatedJualTotal)}`}
                    />
                  </div>
                  <p className="text-[10px] text-white/50 mt-1.5">Harga yang akan ditagihkan ke Client (Customer).</p>
                </div>
              </div>

              {/* ASA AI Recommendation */}
              <div className="bg-[#FFFDF9] border border-[#E6D5B8] rounded-xl p-4 shadow-sm mb-6 relative z-10">
                <div className="flex items-center gap-2 text-[#8C6208] mb-2 font-bold text-xs">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center shrink-0">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  ASA (AI) Rekomendasi
                </div>
                <p className="text-xs text-[#7A695B] leading-relaxed italic border-l-2 border-[#D4AF37] pl-3">"{getAsaRecommendation()}"</p>
              </div>

              {/* Rincian Harga per pcs */}
              <div className="space-y-2 text-xs text-white/80 mb-6 relative z-10 border-t border-white/10 pt-4">
                <div className="flex justify-between items-center text-white/50">
                  <span>Modal Kemasan (Packaging)</span>
                  <span>Rp {fmt(modalPackaging)}</span>
                </div>
                {(product.is_fullset && jualPackagingManual > modalPackaging) && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Fullset</span>
                    <span>Rp {fmt(jualPackagingManual - modalPackaging)}</span>
                  </div>
                )}
                {(!isDiscoverySet && !product.is_fullset && (jualBotol > modalBotol || jualTutup > modalTutup || jualSpray > modalSpray)) && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Kemasan</span>
                    <span>Rp {fmt((jualBotol - modalBotol) + (jualTutup - modalTutup) + (jualSpray - modalSpray))}</span>
                  </div>
                )}
                {(isDiscoverySet && jualPackagingManual > modalPackaging) && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Kemasan</span>
                    <span>Rp {fmt(jualPackagingManual - modalPackaging)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-white/50 mt-1">
                  <span>Modal Formula Cairan ({ukuranLabel}ml)</span>
                  <span>Rp {fmt(modalRacikan)}</span>
                </div>
                {jualRacikan > modalRacikan && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Cairan</span>
                    <span>Rp {fmt(jualRacikan - modalRacikan)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-white/50 mt-1">
                  <span>Modal Kelengkapan Tambahan</span>
                  <span>Rp {fmt(modalKomponen)}</span>
                </div>
                {jualKomponen > modalKomponen && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Kelengkapan Tambahan</span>
                    <span>Rp {fmt(jualKomponen - modalKomponen)}</span>
                  </div>
                )}
                {jasaNominal > 0 && (
                  <div className="flex justify-between items-center text-[#D4AF37] mt-1">
                    <span>Biaya Jasa Produksi</span>
                    <span>Rp {fmt(jasaNominal)}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center pt-3 text-white/50 border-t border-white/10 mt-2">
                  <span>Total Modal Asli / Beli (per pcs)</span>
                  <span>Rp {fmt(modalMurni)}</span>
                </div>
                <div className="flex justify-between items-center pt-1 font-bold text-white">
                  <span>Total Harga Sistem Jual (per pcs)</span>
                  <span>Rp {fmt(calculatedJualTotal)}</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end relative z-10">
                <div className={`p-3 rounded-xl border ${marginNominal >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <p className="text-[10px] text-white/70 mb-1">Margin Profit (per pcs)</p>
                  <p className={`text-xl font-bold ${marginNominal >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {marginNominal >= 0 ? '+' : ''}Rp {fmt(marginNominal)}
                  </p>
                  <p className={`text-[10px] ${marginNominal >= 0 ? 'text-emerald-400/70' : 'text-red-400/70'} mt-0.5`}>
                    {marginPersen.toFixed(1)}% dari Total Modal
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/70 mb-1">Grand Total ({product.qty || 1} pcs)</p>
                  <p className="text-3xl font-black text-[#D4AF37]">Rp {fmt(finalHargaJualPerPcs * (product.qty || 1))}</p>
                </div>
              </div>

              {/* Kesimpulan Kebutuhan */}
              <div className="mt-6 pt-6 border-t border-white/20 relative z-10">
                <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4">
                  Kesimpulan Kebutuhan ({product.qty || 1} pcs)
                </h4>
                
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${(product.komponen_tambahan && product.komponen_tambahan.length > 0) ? 'lg:grid-cols-3' : ''} gap-4 mb-4`}>
                  {/* Kemasan */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/80">
                    <p className="font-bold text-white mb-2 pb-1 border-b border-white/10">Kebutuhan Kemasan</p>
                    {product.is_fullset ? (
                      <div className="flex justify-between items-center mt-2">
                        <span className="truncate pr-2">{fullset?.nama || 'Fullset'}</span>
                        <span className="shrink-0 text-right font-medium">{fmtQty(product.qty || 1)} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * modalPackaging)}</span></span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mt-2">
                          <span className="truncate pr-2">{botol?.nama || 'Botol'}</span>
                          <span className="shrink-0 text-right font-medium">{fmtQty(product.qty || 1)} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * modalBotol)}</span></span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="truncate pr-2">{tutup?.nama || 'Tutup'}</span>
                          <span className="shrink-0 text-right font-medium">{fmtQty(product.qty || 1)} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * modalTutup)}</span></span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="truncate pr-2">{spray?.nama || 'Spray'}</span>
                          <span className="shrink-0 text-right font-medium">{fmtQty(product.qty || 1)} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * modalSpray)}</span></span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-bold text-[#D4AF37]">
                      <span>Total Modal Kemasan</span>
                      <span>Rp {fmt((product.qty || 1) * modalPackaging)}</span>
                    </div>
                  </div>

                  {/* Formula Cairan */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/80">
                    <p className="font-bold text-white mb-2 pb-1 border-b border-white/10">Kebutuhan Cairan ({ukuranLabel}ml)</p>
                    
                    <div className="space-y-2 mt-2">
                      {(product.racikan || []).map((r, i) => {
                        if (!r.bahan_id || !r.persentase) return null;
                        const volPerPcs = (safeFloat(r.persentase) / 100) * ukuranLabel;
                        const totalVol = volPerPcs * (product.qty || 1);
                        
                        const isFluid = needsLiterConversion(r.satuan, r.kategori) || r.satuan === 'liter' || r.satuan === 'g' || r.satuan === 'ml' || r.kategori?.toLowerCase().includes('alkohol') || r.kategori?.toLowerCase().includes('aqua');
                        
                        let displayVol = '';
                        if (isFluid) {
                          const converted = formatGramMl(totalVol, 100);
                          displayVol = converted?.label || `${(totalVol / 1000).toFixed(2).replace('.', ',')} L`;
                        } else {
                          displayVol = totalVol >= 100 
                            ? `${(totalVol / 1000).toFixed(2).replace('.', ',')} kg` 
                            : `${totalVol.toFixed(1).replace('.', ',')} g`;
                        }
                        
                        const isLiterCost = r.satuan === 'g'; 
                        const hargaBeliSatuan = r.harga_per_satuan || 0;
                        const perMl = isLiterCost ? hargaBeliSatuan / 1000 : ((r.satuan === 'kg' || r.satuan === 'liter') ? hargaBeliSatuan / 1000 : hargaBeliSatuan);
                        const cost = perMl * volPerPcs * (product.qty || 1);

                        return (
                          <div key={i} className="flex justify-between items-center">
                            <span className="truncate pr-2">{r.bahan_nama}</span>
                            <span className="shrink-0 text-right font-medium">{displayVol} <span className="text-white/50 text-[10px] block font-normal">Rp {fmt(cost)}</span></span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-bold text-[#D4AF37]">
                      <span>Total Modal Cairan</span>
                      <span>Rp {fmt((product.qty || 1) * modalRacikan)}</span>
                    </div>
                  </div>

                  {/* Tambahan & Jasa */}
                  {(product.komponen_tambahan && product.komponen_tambahan.length > 0) && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/80">
                      <p className="font-bold text-white mb-2 pb-1 border-b border-white/10">Kelengkapan Tambahan</p>
                      
                      <div className="space-y-2 mt-2">
                        {product.komponen_tambahan.map((k, i) => (
                          <div key={i} className="flex justify-between items-center">
                            <span className="truncate pr-2">{k.nama || k.kategori}</span>
                            <span className="shrink-0 text-right font-medium">{fmtQty(product.qty || 1)} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * (k.harga || 0))}</span></span>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-bold text-[#D4AF37]">
                        <span>Total Modal Tambahan</span>
                        <span>Rp {fmt((product.qty || 1) * modalKomponen)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 text-xs">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="space-y-1.5 flex-1">
                      <div className="flex justify-between text-white/70">
                        <span>Total Harga Beli / Modal Keseluruhan</span>
                        <span>Rp {fmt((product.qty || 1) * modalMurni)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-[#D4AF37]">
                        <span>Total Harga Jual Keseluruhan</span>
                        <span>Rp {fmt((product.qty || 1) * finalHargaJualPerPcs)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-emerald-400 pt-1 border-t border-white/10 mt-1">
                        <span>Total Margin Profit</span>
                        <span>Rp {fmt((product.qty || 1) * marginNominal)} ({marginPersen.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="sm:w-1/3 bg-black/20 p-3 rounded-lg border border-white/5 flex flex-col justify-center items-center text-center">
                       <p className="text-white/60 mb-1">Catatan Profit:</p>
                       <p className="text-[#D4AF37] font-bold">Margin profit keseluruhan ini sudah termasuk pendapatan dari Total Biaya Jasa Produksi sebesar Rp {fmt((product.qty || 1) * jasaNominal)} (Rp {fmt(jasaNominal)} × {product.qty || 1} pcs).</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
